import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';

import {
  UTILITY_USER_ROLE_ADMIN,
  UTILITY_USER_ROLE_PARTNER_ADMIN,
  UTILITY_USER_ROLE_PARTNER_ACCOUNTING,
} from 'common/utility/userRoles';

import { SCREEN_ID_UTILITY_PARTNER_SETTINGS_BANKS } from 'client/constants/screenIds';

import allowedUtilityRoles from 'client/helpers/allowedUtilityRoles';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import branch from 'client/core/branch';

import UtilityPartnerActions from 'client/actions/UtilityPartner';

import SrBox from 'client/components/SrBox';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import UPPartnerBankAccountConnected from 'client/components/UPConnectBankForm/UPPartnerBankAccountConnected';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrButton, {
  SR_BUTTON_HEIGHT_TALL,
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';
import SrCard, { SR_CARD_FONT_SIZE_SMALL } from 'client/components/SrCard/SrCard';

@withTransitionHook
@allowedUtilityRoles({
  roles: [
    UTILITY_USER_ROLE_ADMIN,
    UTILITY_USER_ROLE_PARTNER_ADMIN,
    UTILITY_USER_ROLE_PARTNER_ACCOUNTING,
  ],
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_UTILITY_PARTNER_SETTINGS_BANKS)
@withScrollToTop
@branch(({ match }) => ({
  utilityUser: ['utilityUser'],
  utilityPartner: ['utilityPartners', 'bySlug', match.params.partnerSlug],
  utilityPartnerStripeAccounts: [
    'utilityPartners',
    'stripeAccountsByPartnerSlug',
    match.params.partnerSlug,
  ],
}))
class UtilityPartnerSettingsBanks extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Settings',
    section: 'Partner',
  });

  static fetchData(match, inject) {
    const { partnerSlug } = match.params;
    const partner = inject.state.get(['partners', 'bySlug', partnerSlug]);

    const promises = [UtilityPartnerActions.getStripeAccounts(inject, { slug: partnerSlug })];

    if (!partner) {
      promises.push(
        UtilityPartnerActions.getBySlug(inject, {
          slug: partnerSlug,
        }),
      );
    }
    return Promise.all(promises);
  }

  static propTypes = {
    utilityPartner: PropTypes.object,
    utilityPartnerStripeAccounts: PropTypes.array,
  };

  constructor(props) {
    super(props);
    this.state = {
      showSavedModal: false,
    };
  }

  render() {
    const { utilityPartner, utilityPartnerStripeAccounts, isTransitioning, history } = this.props;
    const { showSavedModal } = this.state;

    if (isTransitioning && !utilityPartner) {
      return null;
    }

    if (!utilityPartner) {
      return <Redirect to="/utility-profit/" />;
    }

    const stripeAccount = !utilityPartner.utilityStripeAccountId
      ? null
      : utilityPartnerStripeAccounts.find((account) => {
          return account.id === Number(utilityPartner.utilityStripeAccountId);
        });

    return (
      <SrPartnerPageV2
        backUrl={`/utility-profit/${utilityPartner.slug}/settings`}
        navbarImage={utilityPartner.logoUrl}
        navbarTitle={utilityPartner.name}
        pageHeader="Payouts"
      >
        {!utilityPartnerStripeAccounts && <div className="loading-ellipses" />}
        {stripeAccount ? (
          <SrBox marginBottom="100px">
            <UPPartnerBankAccountConnected
              stripeAccount={stripeAccount}
              utilityPartnerSlug={utilityPartner.slug}
            />
          </SrBox>
        ) : (
          <SrCard headerSizeType={SR_CARD_FONT_SIZE_SMALL} header="Add collection method">
            <SrBox marginTop="21px">
              <SrButton
                theme={SR_BUTTON_THEME_ADOBE_GREEN}
                heightType={SR_BUTTON_HEIGHT_TALL}
                hasBoxShadow
                type="button"
                onClick={() => history.push(`/utility-profit/${utilityPartner.slug}/connect-bank`)}
              >
                Connect Debit or Bank
              </SrButton>
            </SrBox>
          </SrCard>
        )}
        {showSavedModal && (
          <SrSimpleMessageModal
            iconUrl="/images/checkmark-circle-large-green.svg"
            headline="Saved!"
            maxWidth={400}
            minWidth={400}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          />
        )}
      </SrPartnerPageV2>
    );
  }
}

UtilityPartnerSettingsBanks.displayName = 'UtilityPartnerSettingsBanks';

export default UtilityPartnerSettingsBanks;
